import type { SubtitleStyle } from '../types/videoEdit';
import type { SubtitleAppearancePresetId } from './subtitleAppearance';

export interface ClipSoundEffect {
  audioUrl: string;
  volume?: number;
}

export type ClipImageStickerRotation = 0 | 90 | 180 | 270;

export interface ClipStickerBase {
  x: number;
  y: number;
  widthRatio: number;
}

export interface ClipImageStickerEffect extends ClipStickerBase {
  type: 'image';
  imageUrl: string;
  heightRatio?: number;
  rotation?: ClipImageStickerRotation;
}

export interface ClipTextStickerStyle {
  appearancePreset?: SubtitleAppearancePresetId;
  color?: string;
  fontId?: string;
  fontName?: string;
  fontUrl?: string;
  bold?: boolean;
  italic?: boolean;
}

export interface ClipTextStickerEffect extends ClipStickerBase {
  type: 'text';
  text: string;
  fontSizeRatio?: number;
  rotation?: number;
  style?: ClipTextStickerStyle;
}

export type ClipStickerEffect = ClipImageStickerEffect | ClipTextStickerEffect;

export interface ClipEffectItem {
  id: string;
  startTime: number;
  endTime: number;
  sticker?: ClipStickerEffect;
  sound?: ClipSoundEffect;
}

export const MAX_CLIP_EFFECT_ITEMS = 5;

export interface ContainedMediaRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function normalizeImageRotation(rotation?: number): ClipImageStickerRotation {
  if (rotation === 90 || rotation === 180 || rotation === 270) {
    return rotation;
  }
  return 0;
}

function normalizeSticker(sticker?: ClipStickerEffect): ClipStickerEffect | undefined {
  if (!sticker) return undefined;

  const stickerType = sticker.type || ('text' in sticker ? 'text' : 'image');
  const x = clamp(sticker.x ?? 0.5, 0, 1);
  const y = clamp(sticker.y ?? 0.5, 0, 1);
  const widthRatio = clamp(sticker.widthRatio ?? 0.2, 0.05, 1);

  if (stickerType === 'text') {
    const textSticker = sticker as ClipTextStickerEffect;
    const text = typeof textSticker.text === 'string' ? textSticker.text.trim() : '';
    if (!text) return undefined;

    return {
      type: 'text',
      text,
      x,
      y,
      widthRatio,
      fontSizeRatio: clamp(textSticker.fontSizeRatio ?? 0.09, 0.03, 0.24),
      rotation: clamp(Number(textSticker.rotation ?? 0), -360, 360),
      style: textSticker.style ? {
        appearancePreset: textSticker.style.appearancePreset,
        color: textSticker.style.color,
        fontId: textSticker.style.fontId,
        fontName: textSticker.style.fontName,
        fontUrl: textSticker.style.fontUrl,
        bold: !!textSticker.style.bold,
        italic: !!textSticker.style.italic,
      } : undefined,
    };
  }

  if (!('imageUrl' in sticker) || !sticker.imageUrl) return undefined;

  const heightRatio = typeof sticker.heightRatio === 'number'
    ? clamp(sticker.heightRatio, 0.05, 1)
    : undefined;

  return {
    type: 'image',
    imageUrl: sticker.imageUrl,
    x,
    y,
    widthRatio,
    heightRatio,
    rotation: normalizeImageRotation(sticker.rotation),
  };
}

function normalizeSound(sound?: ClipSoundEffect): ClipSoundEffect | undefined {
  if (!sound?.audioUrl) return undefined;
  return {
    audioUrl: sound.audioUrl,
    volume: clamp(sound.volume ?? 1, 0, 1),
  };
}

export function normalizeClipEffectItems(
  effectItems: ClipEffectItem[] | string | undefined,
  clipDuration?: number,
): ClipEffectItem[] {
  const parsed = typeof effectItems === 'string'
    ? (() => {
      try {
        return JSON.parse(effectItems) as ClipEffectItem[];
      } catch {
        return [];
      }
    })()
    : effectItems;

  if (!Array.isArray(parsed)) return [];

  const maxEnd = typeof clipDuration === 'number' && clipDuration > 0 ? clipDuration : Number.POSITIVE_INFINITY;

  return parsed
    .map((item, index) => {
      const startTime = clamp(Number(item.startTime ?? 0), 0, maxEnd);
      const endTime = clamp(Number(item.endTime ?? startTime), startTime, maxEnd);
      const sticker = normalizeSticker(item.sticker);
      const sound = normalizeSound(item.sound);

      return {
        id: item.id || `effect-${Date.now()}-${index}`,
        startTime,
        endTime,
        sticker,
        sound,
      } satisfies ClipEffectItem;
    })
    .filter((item) => item.endTime > item.startTime)
    .filter((item) => item.sticker || item.sound)
    .sort((a, b) => a.startTime - b.startTime)
    .slice(0, MAX_CLIP_EFFECT_ITEMS);
}

export function isClipEffectActive(effectItem: ClipEffectItem, localTime: number) {
  return localTime >= effectItem.startTime && localTime < effectItem.endTime;
}

export function getActiveClipEffectItems(
  effectItems: ClipEffectItem[] | string | undefined,
  localTime: number,
  clipDuration?: number,
) {
  return normalizeClipEffectItems(effectItems, clipDuration).filter((item) => isClipEffectActive(item, localTime));
}

export function getClipEffectItemById(
  effectItems: ClipEffectItem[] | string | undefined,
  effectItemId: string,
  clipDuration?: number,
) {
  return normalizeClipEffectItems(effectItems, clipDuration).find((item) => item.id === effectItemId);
}

export function getContainedMediaRect(
  containerWidth: number,
  containerHeight: number,
  mediaAspectRatio: number,
): ContainedMediaRect {
  if (containerWidth <= 0 || containerHeight <= 0 || mediaAspectRatio <= 0) {
    return { left: 0, top: 0, width: containerWidth, height: containerHeight };
  }

  const containerAspectRatio = containerWidth / containerHeight;

  if (containerAspectRatio > mediaAspectRatio) {
    const height = containerHeight;
    const width = height * mediaAspectRatio;
    return {
      left: (containerWidth - width) / 2,
      top: 0,
      width,
      height,
    };
  }

  const width = containerWidth;
  const height = width / mediaAspectRatio;
  return {
    left: 0,
    top: (containerHeight - height) / 2,
    width,
    height,
  };
}

export function resolveClipTextStickerStyle(
  sticker: ClipTextStickerEffect | undefined,
  baseSubtitleStyle?: SubtitleStyle,
): SubtitleStyle {
  return {
    fontSize: 18,
    color: sticker?.style?.color || baseSubtitleStyle?.color || '#FFFFFF',
    position: 'center',
    background: false,
    appearancePreset: sticker?.style?.appearancePreset || baseSubtitleStyle?.appearancePreset || 'white_black',
    fontId: sticker?.style?.fontId || baseSubtitleStyle?.fontId,
    fontName: sticker?.style?.fontName || baseSubtitleStyle?.fontName,
    fontUrl: sticker?.style?.fontUrl || baseSubtitleStyle?.fontUrl,
  };
}
